// Smart location resolution for different geometry types
import { reverseGeocode, ReverseGeocodeResult } from './revgeo';

export interface ResolvedLocation {
  label: string;
  fullLabel: string;
  level: 'street' | 'neighborhood' | 'district' | 'city' | 'region' | 'country' | 'continent' | 'unknown';
  components: {
    road?: string;
    neighborhood?: string;
    district?: string;
    city?: string;
    region?: string;
    country?: string;
    continent?: string;
  };
}

// Address hierarchy levels from most specific to most general
const ADDRESS_LEVELS = ['road', 'neighborhood', 'district', 'city', 'region', 'country', 'continent'] as const;
type AddressLevel = typeof ADDRESS_LEVELS[number];

interface AddressComponentInfo {
  raw: string;
  normalized: string;
}

type AddressComponentMap = {
  [K in AddressLevel]?: AddressComponentInfo;
};

// Continent mapping for fallback
const COUNTRY_TO_CONTINENT: Record<string, string> = {
  // Europe
  'United Kingdom': 'Europe', 'Germany': 'Europe', 'France': 'Europe', 'Italy': 'Europe', 'Spain': 'Europe',
  'Netherlands': 'Europe', 'Belgium': 'Europe', 'Switzerland': 'Europe', 'Austria': 'Europe', 'Portugal': 'Europe',
  'Poland': 'Europe', 'Czech Republic': 'Europe', 'Hungary': 'Europe', 'Romania': 'Europe', 'Bulgaria': 'Europe',
  'Greece': 'Europe', 'Croatia': 'Europe', 'Serbia': 'Europe', 'Slovakia': 'Europe', 'Slovenia': 'Europe',
  'Lithuania': 'Europe', 'Latvia': 'Europe', 'Estonia': 'Europe', 'Finland': 'Europe', 'Sweden': 'Europe',
  'Norway': 'Europe', 'Denmark': 'Europe', 'Iceland': 'Europe', 'Ireland': 'Europe', 'Luxembourg': 'Europe',
  'Malta': 'Europe', 'Cyprus': 'Europe', 'Russia': 'Europe', 'Ukraine': 'Europe', 'Belarus': 'Europe',

  // North America
  'United States': 'North America', 'Canada': 'North America', 'Mexico': 'North America',
  'United States of America': 'North America', 'USA': 'North America',

  // Asia
  'China': 'Asia', 'Japan': 'Asia', 'India': 'Asia', 'South Korea': 'Asia', 'Thailand': 'Asia',
  'Vietnam': 'Asia', 'Singapore': 'Asia', 'Malaysia': 'Asia', 'Indonesia': 'Asia', 'Philippines': 'Asia',
  'Taiwan': 'Asia', 'Hong Kong': 'Asia', 'Mongolia': 'Asia', 'Kazakhstan': 'Asia', 'Uzbekistan': 'Asia',
  'Pakistan': 'Asia', 'Bangladesh': 'Asia', 'Sri Lanka': 'Asia', 'Myanmar': 'Asia', 'Cambodia': 'Asia',
  'Laos': 'Asia', 'Nepal': 'Asia', 'Bhutan': 'Asia', 'Afghanistan': 'Asia', 'Iran': 'Asia',
  'Iraq': 'Asia', 'Saudi Arabia': 'Asia', 'United Arab Emirates': 'Asia', 'Qatar': 'Asia', 'Kuwait': 'Asia',
  'Bahrain': 'Asia', 'Oman': 'Asia', 'Yemen': 'Asia', 'Jordan': 'Asia', 'Lebanon': 'Asia', 'Syria': 'Asia',
  'Turkey': 'Asia', 'Israel': 'Asia', 'Palestine': 'Asia',

  // Africa
  'South Africa': 'Africa', 'Nigeria': 'Africa', 'Egypt': 'Africa', 'Kenya': 'Africa', 'Morocco': 'Africa',
  'Algeria': 'Africa', 'Tunisia': 'Africa', 'Libya': 'Africa', 'Sudan': 'Africa', 'Ethiopia': 'Africa',
  'Ghana': 'Africa', 'Tanzania': 'Africa', 'Uganda': 'Africa', 'Madagascar': 'Africa', 'Cameroon': 'Africa',
  'Ivory Coast': 'Africa', 'Niger': 'Africa', 'Mali': 'Africa', 'Burkina Faso': 'Africa', 'Malawi': 'Africa',
  'Zambia': 'Africa', 'Senegal': 'Africa', 'Somalia': 'Africa', 'Chad': 'Africa', 'Guinea': 'Africa',
  'Rwanda': 'Africa', 'Benin': 'Africa', 'Burundi': 'Africa', 'Togo': 'Africa',
  'Sierra Leone': 'Africa', 'Liberia': 'Africa', 'Central African Republic': 'Africa', 'Mauritania': 'Africa',
  'Eritrea': 'Africa', 'Gambia': 'Africa', 'Botswana': 'Africa', 'Gabon': 'Africa', 'Lesotho': 'Africa',
  'Guinea-Bissau': 'Africa', 'Equatorial Guinea': 'Africa', 'Mauritius': 'Africa', 'Eswatini': 'Africa',
  'Djibouti': 'Africa', 'Comoros': 'Africa', 'Cape Verde': 'Africa', 'São Tomé and Príncipe': 'Africa',
  'Seychelles': 'Africa', 'Angola': 'Africa', 'Democratic Republic of the Congo': 'Africa', 'Republic of the Congo': 'Africa',
  'Zimbabwe': 'Africa', 'Namibia': 'Africa', 'Mozambique': 'Africa',

  // South America
  'Brazil': 'South America', 'Argentina': 'South America', 'Chile': 'South America', 'Peru': 'South America',
  'Colombia': 'South America', 'Venezuela': 'South America', 'Ecuador': 'South America', 'Bolivia': 'South America',
  'Paraguay': 'South America', 'Uruguay': 'South America', 'Guyana': 'South America', 'Suriname': 'South America',
  'French Guiana': 'South America',

  // Oceania
  'Australia': 'Oceania', 'New Zealand': 'Oceania', 'Papua New Guinea': 'Oceania', 'Fiji': 'Oceania',
  'Solomon Islands': 'Oceania', 'Vanuatu': 'Oceania', 'Samoa': 'Oceania', 'Tonga': 'Oceania',
  'Kiribati': 'Oceania', 'Palau': 'Oceania', 'Marshall Islands': 'Oceania', 'Tuvalu': 'Oceania',
  'Nauru': 'Oceania', 'Micronesia': 'Oceania',

  // Antarctica
  'Antarctica': 'Antarctica'
};

/**
 * Normalize address component for comparison
 */
const normalizeComponent = (value: string | undefined | null): string | undefined => {
  if (typeof value !== 'string') return undefined;
  // Basic normalization: trim, lowercase, remove punctuation, collapse spaces, fold accents
  let s = value.normalize('NFD').replace(/\p{Diacritic}+/gu, ''); // remove accents
  s = s.trim().toLowerCase();
  if (!s) return undefined;
  s = s.replace(/[.,]/g, ' '); // strip common punctuation
  s = s.replace(/\s+/g, ' ');   // collapse whitespace
  return s;
};

const toComponentInfo = (value: string | undefined | null): AddressComponentInfo | undefined => {
  const normalized = normalizeComponent(value);
  if (!normalized) return undefined;
  return {
    raw: (value as string).trim(),
    normalized
  };
};

/**
 * Extract structured address components from ReverseGeocodeResult with alias harmonization
 */
const extractComponents = (result: ReverseGeocodeResult): AddressComponentMap => {
  // Neighborhood-like granularity
  const hood = result.neighborhood
    || (result.label?.includes(',') ? result.label.split(',')[0]?.trim() : undefined);

  // Region-like granularity (state/county/province)
  const region = result.region;

  // District-like granularity: unify common provider variants
  // ReverseGeocodeResult already collapses some in composeLabel; keep result.district as canonical
  const district = result.district;

  // City-like granularity: prefer explicit city, else fall back to district when it acts as city-scale
  const city = result.city || district;

  // Continent via explicit field or derived from country
  const continent = result.continent || (result.country ? COUNTRY_TO_CONTINENT[result.country] : undefined);

  return {
    road: toComponentInfo(result.road),
    neighborhood: toComponentInfo(hood),
    district: toComponentInfo(district),
    city: toComponentInfo(city),
    region: toComponentInfo(region),
    country: toComponentInfo(result.country),
    continent: toComponentInfo(continent)
  };
};

/**
 * Find the most specific common address level between multiple addresses
 */
export const findCommonAddressLevel = (addresses: ReverseGeocodeResult[]): ResolvedLocation => {
  if (addresses.length === 0) {
    return {
      label: 'Unknown Location',
      fullLabel: 'Unknown Location',
      level: 'unknown',
      components: {}
    };
  }

  // Component-wise intersection across all addresses (no sequential dependency)
  const componentMaps = addresses.map(extractComponents);
  const components: ResolvedLocation['components'] = {};

  const matched: Partial<Record<AddressLevel, string>> = {};
  for (const key of ADDRESS_LEVELS) {
    const values = componentMaps.map(cm => cm[key]?.normalized).filter(Boolean) as string[];
    if (values.length !== addresses.length) continue; // missing in one → not a match
    const first = values[0];
    if (values.every(v => v === first)) {
      matched[key] = componentMaps[0][key]!.raw;
      (components as any)[key] = componentMaps[0][key]!.raw;
    }
  }

  // Compose label from any matched components, prioritizing city → district → neighborhood → road → region → country
  const order: AddressLevel[] = ['city', 'district', 'neighborhood', 'road', 'region', 'country'];
  const labelParts: string[] = [];
  const seen = new Set<string>();
  for (const k of order) {
    const raw = matched[k];
    if (!raw) continue;
    const n = (normalizeComponent(raw) || '');
    if (n && !seen.has(n)) { labelParts.push(raw); seen.add(n); }
  }
  let label = labelParts.join(', ');
  // fullLabel: append continent if present and not duplicate
  const fullList = [...labelParts];
  if (matched.country && !fullList.map(s => s.toLowerCase()).includes(matched.country.toLowerCase())) fullList.push(matched.country);
  if (matched.continent && !fullList.map(s => s.toLowerCase()).includes(matched.continent.toLowerCase())) fullList.push(matched.continent);
  let fullLabel = fullList.join(', ') || label;
  // Level: pick the most specific present in order
  let level: ResolvedLocation['level'] = 'unknown';
  if (matched.road) level = 'street';
  else if (matched.neighborhood) level = 'neighborhood';
  else if (matched.district) level = 'district';
  else if (matched.city) level = 'city';
  else if (matched.region) level = 'region';
  else if (matched.country) level = 'country';

  // No majority fallback: strictly intersection-only as requested

  if (!label) {
    const waterRegex = /(sea|ocean|gulf|bay|strait|channel|sound|lake)/i;
    const waterAddress = addresses.find(addr => waterRegex.test(addr.label || '') || waterRegex.test(addr.fullLabel || ''));

    if (waterAddress) {
      label = waterAddress.label || waterAddress.fullLabel || 'Open Water';
      fullLabel = waterAddress.fullLabel || label;
      level = 'unknown';
    } else {
      const continentValues = componentMaps
        .map(map => map.continent?.raw)
        .filter(Boolean) as string[];
      const uniqueContinents = Array.from(new Set(continentValues.map(value => value.trim())));

      if (uniqueContinents.length === 1) {
        label = uniqueContinents[0];
        fullLabel = label;
        level = 'continent';
        components.continent = label;
      } else if (uniqueContinents.length > 1) {
        label = uniqueContinents.join(' / ');
        fullLabel = label;
        level = 'continent';
      } else {
        label = addresses[0].label || addresses[0].fullLabel || 'Unknown Location';
        fullLabel = addresses[0].fullLabel || label;
        level = 'unknown';
      }
    }
  } else if (addresses.length === 1) {
    // Preserve detailed full label for single-point lookups
    fullLabel = addresses[0].fullLabel || label;
  }

  if (!label) {
    label = 'Unknown Location';
    fullLabel = 'Unknown Location';
    level = 'unknown';
  }

  return { label, fullLabel, level, components };
};

/**
 * Extract coordinates from GeoJSON geometry
 */
const extractCoordinates = (geometry: any): [number, number][] => {
  if (!geometry || !geometry.coordinates) return [];

  switch (geometry.type) {
    case 'Point':
      return [geometry.coordinates as [number, number]];

    case 'LineString':
      return geometry.coordinates as [number, number][];

    case 'Polygon':
      // Return coordinates of the outer ring (without duplicated closing vertex if present)
      {
        const ring = geometry.coordinates[0] as [number, number][];
        if (ring && ring.length > 1) {
          const a = ring[0];
          const b = ring[ring.length - 1];
          if (a && b && a[0] === b[0] && a[1] === b[1]) return ring.slice(0, -1);
        }
        return ring;
      }

    case 'MultiPoint':
      return geometry.coordinates as [number, number][];

    case 'MultiLineString':
      // Flatten all line strings
      return geometry.coordinates.flat() as [number, number][];

    case 'MultiPolygon':
      // Flatten all polygons (outer rings only)
      return geometry.coordinates.map((polygon: any) => polygon[0]).flat() as [number, number][];

    default:
      return [];
  }
};

/**
 * Get representative points for different geometry types
 */
const getRepresentativePoints = (geometry: any): [number, number][] => {
  const coords = extractCoordinates(geometry);
  if (coords.length === 0) return [];

  switch (geometry.type) {
    case 'Point':
      return coords;

    case 'LineString':
      // Use exactly the farthest two points (matches requested logic)
      if (coords.length < 2) return coords;
      {
        let maxD = -1, iMax = 0, jMax = 1;
        for (let i = 0; i < coords.length; i++) {
          for (let j = i + 1; j < coords.length; j++) {
            const dx = coords[i][0] - coords[j][0];
            const dy = coords[i][1] - coords[j][1];
            const d = dx * dx + dy * dy;
            if (d > maxD) { maxD = d; iMax = i; jMax = j; }
          }
        }
        const a = coords[iMax], b = coords[jMax];
        return [a, b];
      }

    case 'Polygon':
    case 'MultiPolygon':
      // Use exactly the farthest two points of the outer ring
      if (coords.length < 2) return coords;
      {
        let maxD = -1, iMax = 0, jMax = 1;
        for (let i = 0; i < coords.length; i++) {
          for (let j = i + 1; j < coords.length; j++) {
            const dx = coords[i][0] - coords[j][0];
            const dy = coords[i][1] - coords[j][1];
            const d = dx * dx + dy * dy;
            if (d > maxD) { maxD = d; iMax = i; jMax = j; }
          }
        }
        const a = coords[iMax], b = coords[jMax];
        return [a, b];
      }

    default:
      return coords.slice(0, 2); // Return up to 2 points for other types
  }
};

/**
 * Resolve location for a geometry (main function)
 */
export const resolveGeometryLocation = async (
  geometryJson: string,
  signal?: AbortSignal
): Promise<ResolvedLocation> => {
  try {
    const geometry = JSON.parse(geometryJson);
    const representativePoints = getRepresentativePoints(geometry);

    if (representativePoints.length === 0) {
      return {
        label: 'Unknown Location',
        fullLabel: 'Unknown Location',
        level: 'unknown',
        components: {}
      };
    }

    // Reverse geocode all representative points
    const reverseGeocodePromises = representativePoints.map(([lng, lat]) =>
      reverseGeocode(lat, lng, signal)
    );

    const addresses = await Promise.all(reverseGeocodePromises);

    // Find common address level
    return findCommonAddressLevel(addresses);

  } catch (error) {
    console.error('Error resolving geometry location:', error);
    return {
      label: 'Location Error',
      fullLabel: 'Location Error',
      level: 'unknown',
      components: {}
    };
  }
};

/**
 * Convenience function to resolve location for MapDrawing
 */
export const resolveDrawingLocation = async (
  drawing: { geometry: string; type: 'POINT' | 'LINE' | 'POLYGON' },
  signal?: AbortSignal
): Promise<ResolvedLocation> => {
  return resolveGeometryLocation(drawing.geometry, signal);
};
